import { useState } from "react"
import styled from "styled-components"
import type { BaseProps } from "~/types/props"
import { type ArbeidsarkIconKeys, type IconType, SVGIcon } from "../Icon"

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  user-select: none;
`

interface TextWrapperProps {
  open: boolean
  iconWidth: string
  iconLeft?: string
  iconTop?: string
  iconColor?: string
}

const TextWrapper = styled.div<TextWrapperProps>`
  display: flex;
  width: 100%;
  align-items: center;
  color: ${(props) => props.theme.colors.primary10};

  gap: 12px;

  height: 3rem;
  opacity: 0.6;
  padding-left: 2.125rem;

  .icon {
    flex-shrink: 0;
    ${(props) =>
      props.iconLeft ? `transform: translate(${props.iconLeft});` : ""}
    ${(props) =>
      props.iconTop ? `transform: translateY(${props.iconTop});` : ""}

    svg {
      fill: ${(props) => props.iconColor || props.theme.colors.primary10};
    }
  }

  &:hover,
  &:focus {
    cursor: pointer;
    color: ${(props) => props.theme.colors.primary20};
    opacity: 1;

    .icon svg {
      fill: ${(props) => props.iconColor || props.theme.colors.primary20};
    }
    .chevron svg {
      fill: ${(props) => props.theme.colors.primary20};
    }
  }

  ${(props) =>
    props.open &&
    `
    color: ${props.theme.colors.primary40};
    background: ${props.theme.colors.primary95};
    opacity: 1;

    .icon svg {
      fill: ${props.iconColor || props.theme.colors.primary40};
    }
    .chevron svg {
      fill: ${props.theme.colors.primary40};
    }
  `}

  .chevron {
    width: 3rem;
    display: flex;
    justify-content: flex-end;
    padding-right: 1rem;
    flex-shrink: 0;

    svg {
      width: 14px;
      height: 16px;
      fill: ${(props) => props.theme.colors.primary10};
    }
  }

  .text-content {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }
`

interface DropdownWrapperProps {
  open: boolean
}
const DropdownWrapper = styled.div<DropdownWrapperProps>`
  display: ${(props) => (props.open ? "block" : "none")};
  padding-left: 1rem;
`

const Badge = styled.span`
  background: ${(props) => props.theme.colors.primary40};
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  text-transform: uppercase;
`

export interface SidebarDropdownProps extends BaseProps {
  icon: ArbeidsarkIconKeys
  iconWidth?: string
  iconLeft?: string
  iconTop?: string
  iconType?: IconType
  iconColor?: string
  text: string
  badge?: string
  children: React.ReactNode
  onClick?: () => void
  defaultOpen?: boolean
}

export const SidebarDropdown: React.CFC<SidebarDropdownProps> = ({
  icon,
  iconWidth = "1rem",
  iconTop,
  iconLeft,
  iconType = "regular",
  iconColor,
  text,
  badge,
  onClick,
  children,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const handleClick = () => {
    setOpen(!open)
  }
  return (
    <Wrapper>
      <TextWrapper
        onClick={handleClick}
        open={open}
        tabIndex={0}
        iconTop={iconTop}
        iconWidth={iconWidth}
        iconLeft={iconLeft}
        iconColor={iconColor}
      >
        <span className="icon">
          <SVGIcon icon={icon} type={iconType} width={iconWidth} />
        </span>
        <span className="text-content">
          {text}
          {badge && <Badge>{badge}</Badge>}
        </span>
        <SVGIcon
          className="chevron"
          icon={open ? "ChevronUp" : "ChevronDown"}
          width="14px"
          type="regular"
        />
      </TextWrapper>
      <DropdownWrapper open={open}>{children}</DropdownWrapper>
    </Wrapper>
  )
}
